import type {
	IconDefinition,
	SizeProp,
} from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';

interface IconProps {
	icon: IconDefinition;
	size?: number | 'xs' | 'sm' | 'lg' | 'xl' | '1x' | '2x' | '3x' | '4x' | '5x';
	color?: string;
	className?: string;
	style?: React.CSSProperties;
	focusable?: boolean;
	variant?:
		| 'primary'
		| 'secondary'
		| 'danger'
		| 'success'
		| 'warning'
		| 'inverse'
		| 'navPrimary'
		| 'navPrimaryActive'
		| 'navSecondary'
		| 'navSecondaryActive';
}

export const Icon: React.FC<IconProps> = ({
	icon,
	size = '1x',
	color,
	className,
	style,
	focusable = false,
	variant,
}) => {
	const colors = useThemeColors();
	// Safety check for undefined icon
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
	if (icon == null) {
		// Warning: Icon component: icon prop is undefined
		return null;
	}

	// Map numeric sizes to FontAwesome size strings
	const getFontAwesomeSize = (): SizeProp => {
		// noinspection SuspiciousTypeOfGuard
		if (typeof size === 'number') {
			if (size <= 12) {
				return 'xs';
			}
			if (size <= 14) {
				return 'sm';
			}
			if (size <= 16) {
				return '1x';
			}
			if (size <= 24) {
				return 'lg';
			}
			if (size <= 32) {
				return '2x';
			}
			return '3x';
		}

		return size;
	};

	// Get color based on variant
	const getVariantClasses = (): string => {
		switch (variant) {
			case 'primary':
				return colors.classes.textPrimary;
			case 'secondary':
				return colors.classes.textSecondary;
			case 'danger':
				return colors.classes.textDanger;
			case 'success':
				return colors.classes.textSuccess;
			case 'warning':
				return colors.classes.textWarning;
			case 'inverse':
				return colors.classes.textWhite;
			case 'navPrimary':
				return colors.classes.textSecondary;
			case 'navPrimaryActive':
				return colors.classes.textPrimary;
			case 'navSecondary':
				return colors.classes.textTertiary;
			case 'navSecondaryActive':
				return colors.classes.textPrimary;
			case undefined:
			default:
				return '';
		}
	};

	return (
		<FontAwesomeIcon
			icon={icon}
			size={getFontAwesomeSize()}
			className={cn(getVariantClasses(), className)}
			style={{ color, ...style }}
			focusable={focusable}
		/>
	);
};
